// overlayState.js — tiny pub/sub for "is a UI overlay open right now?"
//
// Used by AdaptiveRail to hide itself when a menu / modal / dropdown
// covers the cellar list (user request 2026-05-26: the rail visually
// crowded the open UserMenu — the menu's z-index already covers the
// rail, but the rail's right-edge chrome still leaks into the periphery
// of the menu's rounded card). Hiding the rail entirely while an
// overlay is open is the iOS-Contacts pattern.
//
// Producers (the components that OPEN overlays) call
// `setOverlayOpen(true)` on open and `setOverlayOpen(false)` on close.
// Consumers (the rail) call `useOverlayOpen()` to subscribe.
//
// We use a window CustomEvent for the bus so the producers and
// consumers don't have to share a React context (UserMenu lives inside
// PageHeader which lives inside virtually every page; threading a
// context through would be invasive). One-line subscribe via
// useSyncExternalStore keeps consumer code clean.
//
// Reference-counted: multiple overlays can be open simultaneously
// (e.g. UserMenu open while a tag-filter dropdown is also open) and
// the rail stays hidden until ALL of them close. Producers pass a
// stable key on open so close cancels the right slot.

import { useSyncExternalStore } from 'react'

const EVT = 'vinarai:overlay-state'
// Set of currently-open overlay keys. Module-scoped so all subscribers
// agree on the truth.
const _openSet = new Set()

function isAnyOpen() { return _openSet.size > 0 }

/**
 * Producer API. Pass a stable identifier ('user-menu', 'tag-filter',
 * etc.) so multi-overlay state is reference-counted correctly. Calling
 * with the same key twice is idempotent.
 *
 * @param {string} key  — stable id for this overlay surface
 * @param {boolean} open
 */
export function setOverlayOpen(key, open) {
  if (!key) return
  const wasOpen = isAnyOpen()
  if (open) _openSet.add(key)
  else      _openSet.delete(key)
  const nowOpen = isAnyOpen()
  if (wasOpen === nowOpen) return  // aggregate state didn't change
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVT, { detail: { open: nowOpen } }))
  }
}

// Snapshot getter for useSyncExternalStore. Returns the boolean — React
// will only re-render when the value actually changes.
function getSnapshot() { return isAnyOpen() }
function getServerSnapshot() { return false }

function subscribe(cb) {
  if (typeof window === 'undefined') return () => {}
  const handler = () => cb()
  window.addEventListener(EVT, handler)
  return () => window.removeEventListener(EVT, handler)
}

/**
 * Consumer hook. Returns `true` when any overlay is currently open,
 * `false` otherwise. Components re-render only when the aggregate
 * flips, not on every per-overlay open/close.
 */
export function useOverlayOpen() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
